import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { LocalMallOutlined, PeopleAltOutlined, ListAlt, AccountBalance as AccountBalanceIcon, AttachMoney } from '@material-ui/icons'
import { useLocation } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'

import { paths } from '@routes'
import { Devices, Spacings } from '@styles'
import { isSalesAdmin, isSuperAdmin, isSupplyAdmin, isTenderBookAdmin } from 'src/utils/adminFilter'
import { CardDetail } from './CompanyReport'
import { DashboardRecord } from '@base'
import { MB_ROLES, monthType, strings } from '@constants'
import { getCompanyDashboardDataV2, getCompanyData, getManagerDashboardData } from '@data'
import { decimalSeparatedNumber, displayAmount, processData, processDataForPieChart } from '@utils'
import { showLoader } from '@data/state/action'
import { getLastTwoMonthsData } from '@base/composite/DashboardRecord'

import { AnalyticsLoader, RenderCharts } from '../../Dashboard'
import { Wrapper } from '../../Dashboard/styles'
import SalesDashboard from './SalesDashboard'
import SupplyDashboard from './SupplyDashboard'

const MainWrapper = styled.div`
	display: grid;
	width: 100%;
	grid-template-columns: repeat(1, 1fr);

	@media ${Devices.tablet} {
		grid-template-columns: repeat(2, 2fr);
		grid-column-gap: ${Spacings.SPACING_4B};
	}
	@media ${Devices.laptop} {
		grid-template-columns: ${({ ShowSideMenu }) => (ShowSideMenu ? `repeat(4, 4fr)` : `repeat(2, 2fr)`)};
	}
`
const Dashboard = () => {
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const [records, SetRecord] = useState()
	const [chartStats, setChartStats] = useState([])
	const { pathname, search } = useLocation()
	const dispatch = useDispatch()
	const showSideMenu = useSelector((state) => state.getIn(['root', 'showSideMenu']))
	const convetWithYear = (arrayData) => {
		if (arrayData?.data.length > 12) {
			const data = arrayData?.data?.map((item) => {
				const _item = item?.label.split(' ')
				if (!_item[0]?.includes('Apr')) {
					return { ...item, label: _item[0] }
				}
				return item
			})
			return { ...arrayData, data }
		}

		return arrayData
	}

	const [accessType, numMonths, employeeId, fromMonth, toMonth, organisationId, platform, organisationName, employeeName] = useMemo(() => {
		const params = new URLSearchParams(search)
		let _accessType = params?.get('access_type') ?? 'OVERALL'
		let _numMonths =
			params?.get('numMonths') ??
			(pathname.includes(paths.admin_dashboard_all) && isSuperAdmin(user.role) ? monthType[2]?.month : monthType[0]?.month)
		let _employeeId = params?.get('employee_id') ?? null
		let _fromMonth = params?.get('from_date')
		let _toMonth = params?.get('to_date')
		let _orgId = params?.get('organisation') ?? null
		let _platform = params?.get('platform')
		let _organisationName = params?.get('organisation') ?? null
		let _employeeName = params?.get('employee_id') ?? null
		return [_accessType, _numMonths, _employeeId, _fromMonth, _toMonth, _orgId, _platform, _organisationName, _employeeName]
	}, [search, pathname, user.role])
	let newUsersCount = getLastTwoMonthsData(records?.total_users, 'unit', { month: numMonths })

	const createChartStatsData = (data) => {
		const newData = []
		if (data.total_enquiries) {
			const barChartData = data?.total_serviced_weight
				? [
						{
							data: data.total_enquiries,
							key: 'enquiry',
						},
						{
							data: data.total_orders,
							key: 'order',
						},
						{
							data: data?.total_serviced_weight,
							key: 'serviced_weight',
						},
				  ]
				: [
						{
							data: data.total_enquiries,
							key: 'enquiry',
						},
						{
							data: data.total_orders,
							key: 'order',
						},
				  ]
			newData.push(
				convetWithYear(
					processData(
						barChartData,
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('monthly_conversion_chart'),
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data.total_enquiries?.length : numMonths
					)
				)
			)
		}
		if (data.total_supplier_quote) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_supplier_quote,
								key: 'quote',
							},
							{
								data: data.total_orders,
								key: 'converted_order',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('monthly_supply_chart'),
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data?.total_supplier_quote?.length : numMonths
					)
				)
			)
		}
		if (data?.total_purchase_business) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_purchase_business,
								key: 'purchase',
							},
							{
								data: data.total_sale_business,
								key: 'sale',
							},
						],
						{
							dataKey: 'amount',
							valueDivisor: 100000, // for converting to lakh
							yLabel: 'Amount in Lakh',
							unit: 'Lakh',
							formatter: displayAmount,
							header: 'Monthly sale / purchase comparison',
							chartType: 'bar',
						},
						numMonths === 'OVERALL' ? data?.total_purchase_business?.length : numMonths
					)
				)
			)
		}
		if (data?.total_enquiries) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_enquiries,
								key: 'enquiry',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('enquiries'),
							chartType: 'line',
							summary: [
								{
									getterKey: 'enquiry',
									label: 'Total:',
									key: 'unit',
								},
								{
									getterKey: 'enquiry',
									label: 'Weight:',
									key: 'weight',
									formatter: decimalSeparatedNumber,
									valueDivisor: 1000,
									unit: 'MT',
								},
							],
						},
						numMonths === 'OVERALL' ? data?.total_enquiries?.length : numMonths
					)
				)
			)
		}

		if (data?.total_orders) {
			newData.push(
				convetWithYear(
					processData(
						[
							{
								data: data.total_orders,
								key: 'order',
							},
						],
						{
							dataKey: 'weight',
							valueDivisor: 1000, // for converting to MT
							yLabel: 'Weight in MT',
							unit: 'MT',
							formatter: decimalSeparatedNumber,
							header: strings('Orders'),
							chartType: 'line',
							summary: [
								{
									getterKey: 'order',
									label: 'Total:',
									key: 'unit',
								},
								{
									getterKey: 'order',
									label: 'Weight:',
									key: 'weight',
									formatter: decimalSeparatedNumber,
									valueDivisor: 1000,
									unit: 'MT',
								},
							],
						},
						numMonths === 'OVERALL' ? data?.total_orders?.length : numMonths
					)
				)
			)
		}

		if (data?.sku_category_wise_data) {
			let _data = data?.order_skuwise_data
			if (data?.order_skuwise_data?.length > 7) {
				// data preprocess -> more than 7 entities for pie chart
				_data = data?.order_skuwise_data?.slice(0, 7)
				let othersWeight = data?.order_skuwise_data?.slice(7)?.reduce((acc, item) => item.weight + acc, 0)
				_data.push({
					unit: null,
					weight: othersWeight,
					amount: null,
					month: null,
					year: null,
					category_name: 'Others',
				})
			}
			newData.push(
				processDataForPieChart(
					{
						data: _data,
					},
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: 'Weight in MT',
						unit: 'MT',
						formatter: (value) => decimalSeparatedNumber(value / 1000),
						header: 'Enquired weight per SKU (category)',
						chartType: 'pie',
						labelKey: 'category_name',
					},
					numMonths === 'OVERALL' ? _data?.length : numMonths
				)
			)
		}

		if (data?.order_skuwise_data) {
			let _data = data?.order_skuwise_data
			if (data?.order_skuwise_data?.length > 7) {
				// data preprocess -> more than 7 entities for pie chart
				_data = data?.order_skuwise_data?.slice(0, 7)
				let othersWeight = data?.order_skuwise_data?.slice(7)?.reduce((acc, item) => item.weight + acc, 0)
				_data.push({
					unit: null,
					weight: othersWeight,
					amount: null,
					month: null,
					year: null,
					category_name: 'Others',
				})
			}
			newData.push(
				processDataForPieChart(
					{
						data: _data,
					},
					{
						dataKey: 'weight',
						valueDivisor: 1000, // for converting to MT
						yLabel: 'Weight in MT',
						unit: 'MT',
						formatter: (value) => decimalSeparatedNumber(value / 1000),
						header: 'Ordered weight per SKU (category)',
						chartType: 'pie',
						labelKey: 'category_name',
					},
					numMonths === 'OVERALL' ? _data.length : numMonths
				)
			)
		}
		setChartStats(newData)
	}

	useEffect(() => {
		if (isTenderBookAdmin(user.role)) {
			return
		}
		if (isSuperAdmin(user.role)) {
			dispatch(showLoader(true))
			getCompanyDashboardDataV2(
				employeeId === 'ALL' ? null : employeeId,
				MB_ROLES.SALES,
				fromMonth,
				toMonth,
				organisationId,
				user?.user_id,
				true,
				platform
			)
				.then((data) => {
					SetRecord(data)
				})
				.catch((err) => {
					console.log(err)
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
			return
		}
		if (isSalesAdmin(user.role, false) || isSupplyAdmin(user.role, false)) {
			dispatch(showLoader(true))
			getCompanyDashboardDataV2(
				employeeId ? employeeId : user?.user_id,
				isSalesAdmin(user.role, false) ? MB_ROLES.SALES : MB_ROLES.SUPPLY,
				fromMonth,
				toMonth,
				organisationId,
				user?.user_id,
				true,
				platform
			)
				.then((data) => {
					SetRecord(data)
				})
				.catch((err) => {
					console.log(err)
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
			return
		}
		getManagerDashboardData(user.user_id, accessType === 'SELF' ? user.user_id : null)
			.then((data) => {
				SetRecord(data)
				createChartStatsData(data)
			})
			.catch((err) => {})
	}, [accessType, numMonths, employeeId, search, toMonth, fromMonth, organisationId, platform])

	const CardsUI = () => {
		const selectMonth = { month: numMonths }
		if (!user.role) return '-'
		if (isSupplyAdmin(user.role, false)) {
			return (
				<MainWrapper ShowSideMenu={showSideMenu}>
					<DashboardRecord
						title={'Total Enquiry'}
						initialData={records?.total_enquiries}
						key={'total_enquiries'}
						keyName={'weight'}
						icon={<ListAlt fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Order'}
						initialData={records?.total_orders}
						key={'total_orders'}
						keyName={'weight'}
						icon={<LocalMallOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Quotes'}
						initialData={records?.total_supplier_quote}
						key={'weight'}
						keyName={'weight'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Quotes Converted'}
						initialData={records?.total_supplier_quote_converted}
						key={'weight'}
						keyName={'weight'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Sale Invoices'}
						initialData={records?.total_sale_business}
						key={'total_sale_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Purchase Invoices'}
						initialData={records?.total_purchase_business}
						key={'total_purchase_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
					/>
					<DashboardRecord
						title={'Total Users'}
						initialData={records?.total_users}
						key={'unit'}
						keyName={'unit'}
						icon={<PeopleAltOutlined fontSize='medium' />}
					/>
				</MainWrapper>
			)
		} else {
			return (
				<MainWrapper ShowSideMenu={showSideMenu}>
					<DashboardRecord
						title={'Total Enquiry'}
						initialData={records?.total_enquiries}
						key={'total_enquiries'}
						keyName={'weight'}
						icon={<ListAlt fontSize='medium' />}
						selectMonth={selectMonth}
						extraData={`(${newUsersCount[0] ? newUsersCount[0] : '0'} New Users)`}
					/>
					<DashboardRecord
						title={'Total Order'}
						initialData={records?.total_orders}
						key={'total_orders'}
						keyName={'weight'}
						icon={<LocalMallOutlined fontSize='medium' />}
						selectMonth={selectMonth}
					/>
					<DashboardRecord
						title={'Total Invoices / Monthly Target (in Crores)'}
						initialData={records?.total_sale_business}
						key={'total_sale_business'}
						keyName={'amount'}
						icon={<AccountBalanceIcon fontSize='medium' />}
						selectMonth={selectMonth}
						extraData={`/ ${displayAmount(records?.monthly_target)}`}
						totalValue={records?.monthly_target}
					/>
					<CardDetail
						heading={'Payments Received / Expected (in Crores)'}
						value={`${displayAmount(records?.payment_received / 10000000)} / ${displayAmount(
							records?.payment_expected_today / 10000000
						)}`}
						icon={<AttachMoney fontSize='medium' />}
						hasDefinition={false}
					/>
				</MainWrapper>
			)
		}
	}

	if (isSuperAdmin(user.role)) return <SalesDashboard dashboardType={MB_ROLES.SALES} data={records} hasOrganisationId={!!organisationId} organisationName={organisationName} employeeName={employeeName} fromDate={fromMonth} toDate={toMonth} />

	if (isSalesAdmin(user.role, false)) return <SalesDashboard dashboardType={MB_ROLES.SALES} data={records} hasOrganisationId={!!organisationId} organisationName={organisationName} employeeName={employeeName} fromDate={fromMonth} toDate={toMonth} />

	if (isSupplyAdmin(user.role, false)) return <SupplyDashboard dashboardType={MB_ROLES.SUPPLY} data={records} />

	return (
		<Wrapper>
			{CardsUI()}
			<AnalyticsLoader data={chartStats} />
			<Wrapper showSideMenu={showSideMenu} fullWidth={numMonths === 'OVERALL'}>
				{/* <RenderCharts showSideMenu={showSideMenu} data={chartStats} vertical={numMonths === 'OVERALL'} /> */}
				<RenderCharts showSideMenu={true} data={chartStats} vertical={numMonths === 'OVERALL'} />
			</Wrapper>
		</Wrapper>
	)
}

export default Dashboard
